"use client";

import { useEffect, useRef, useState } from "react";
import { CheckoutDialog } from "@/components/checkout_dialog";
import { PricingCardArtistic } from "@/components/pricing/pricing_card_artistic";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import type { PricingRecord } from "@/lib/pricing-data";
import { findInitialCheckoutPlan, toCheckoutPlan } from "./checkout_launch";
import PriceMatchDialog from "./price_match_dialog";

export default function ReachPricingTab({
	data,
	country = "usd",
	initialCheckoutPlanId = null,
}: {
	data: PricingRecord[];
	country?: string;
	initialCheckoutPlanId?: number | null;
}) {
	const [selectedPlan, setSelectedPlan] = useState<PricingRecord | null>(null);
	const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
	const openedInitialPlanId = useRef<number | null>(null);

	const handleGetStarted = (plan: PricingRecord) => {
		setSelectedPlan(plan);
		setIsCheckoutOpen(true);
	};

	useEffect(() => {
		if (
			!initialCheckoutPlanId ||
			openedInitialPlanId.current === initialCheckoutPlanId
		) {
			return;
		}

		const initialPlan = findInitialCheckoutPlan(
			data ?? [],
			initialCheckoutPlanId,
		);
		if (!initialPlan) return;

		setSelectedPlan(initialPlan);
		setIsCheckoutOpen(true);
		openedInitialPlanId.current = initialCheckoutPlanId;
	}, [data, initialCheckoutPlanId]);

	return (
		<>
			<CheckoutDialog
				open={isCheckoutOpen}
				onOpenChange={setIsCheckoutOpen}
				plan={selectedPlan ? toCheckoutPlan(selectedPlan) : null}
				currency={country === "inr" ? "inr" : "usd"}
			/>

			<div className="w-full max-w-3xl mx-auto text-center mb-10">
				<p className="font-inter text-zinc-500 text-base leading-relaxed">
					Run targeted Google Video Ads to get your content in front of real
					viewers in your niche.
				</p>
			</div>

			<section className="w-full mb-12">
				<div
					className={`grid grid-cols-1 md:grid-cols-2 ${
						(data?.length ?? 0) % 3 === 0 ? "lg:grid-cols-3" : "lg:grid-cols-4"
					} gap-6 max-w-7xl mx-auto`}
				>
					{(data ?? []).map((pkg, i) => (
						<div key={pkg.id} className="h-full">
							<PricingCardArtistic
								name={pkg.tier || pkg.name}
								price={country === "inr" ? pkg.price_in_inr : pkg.price_in_usd}
								currency={country}
								features={pkg.features}
								popular={pkg.popular}
								onGetStarted={() => handleGetStarted(pkg)}
								index={i}
								description="Video Ad Campaign"
								categoryIcon="play"
							/>
						</div>
					))}
				</div>
			</section>

			{/* Requirement Note */}
			<div className="w-full max-w-7xl mx-auto mb-8 mt-4">
				<div className="flex items-center justify-center gap-2 text-sm text-zinc-500 font-inter">
					<span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
					<span>
						{highlightPricingTextFeatures(
							"Video must be under 5 minutes, as longer videos may result in higher campaign costs.",
						)}
					</span>
				</div>
			</div>

			<PriceMatchDialog />

			<div className="w-full max-w-3xl mx-auto mb-20">
				<div className="text-center mb-12">
					<h2 className="font-syne font-black text-3xl uppercase tracking-tight text-zinc-900">
						Common Inquiries
					</h2>
					<div className="h-1 w-12 bg-zinc-200 mx-auto mt-4 rounded-full" />
				</div>

				<Accordion type="single" collapsible className="w-full space-y-4">
					<AccordionItem
						value="item-1"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Safety Protocols & Compliance
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							We follow Google Ads best practices and platform guidelines to
							keep your account safe. Every campaign runs through official
							channels.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-2"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Activation Timeline
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Campaigns typically launch within 12-24 hours. During peak periods, slight delays may occur as we manage campaign volume.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-3"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Audience Retention
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							Our campaigns target audiences with genuine interest in your
							content niche, driving meaningful engagement that contributes to
							long-term channel growth.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-4"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Campaign Strategy
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"For best results, we recommend focusing your campaign budget on your strongest content. This lets us optimize targeting and deliver the most engaged audience for your videos.",
							)}
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		</>
	);
}
